"use client"

import { useState } from "react"
import { Button, Heading } from "@medusajs/ui"

import CartTotals from "@modules/common/components/cart-totals"
import Divider from "@modules/common/components/divider"
import DiscountCode from "@modules/checkout/components/discount-code"
import LocalizedClientLink from "@modules/common/components/localized-client-link"
import { HttpTypes } from "@medusajs/types"
import { useTenant } from "@multitenancy/TenantAndVariantContext"
import { createPartialCheckoutCart } from "@lib/data/cart"
import { useParams, useRouter } from "next/navigation"

type SummaryProps = {
  cart: HttpTypes.StoreCart & {
    promotions: HttpTypes.StorePromotion[]
  }
  checkedIds?: Set<string>
  hasPartialSelection?: boolean
}

function getCheckoutStep(cart: HttpTypes.StoreCart) {
  if (!cart?.shipping_address?.address_1 || !cart.email) {
    return "address"
  } else if (cart?.shipping_methods?.length === 0) {
    return "delivery"
  } else {
    return "payment"
  }
}

const Summary = ({ cart, checkedIds, hasPartialSelection }: SummaryProps) => {
  const step = getCheckoutStep(cart)
  const storeConfig = useTenant()
  const router = useRouter()
  const countryCode = useParams().countryCode as string
  const [checkingOut, setCheckingOut] = useState(false)

  const items = cart.items || []
  const selectedItems = checkedIds && checkedIds.size > 0
    ? items.filter((i) => checkedIds.has(i.id))
    : items

  const selectedSubtotal = selectedItems.reduce((sum, item) => {
    return sum + (item.subtotal || 0)
  }, 0)

  const selectedCount = selectedItems.length

  // Handle partial checkout: create temp cart with selected items, keep original intact
  const handlePartialCheckout = async () => {
    if (!checkedIds || checkedIds.size === 0) return
    setCheckingOut(true)
    try {
      await createPartialCheckoutCart(Array.from(checkedIds), countryCode)
      router.push(`/${countryCode}/checkout?step=address`)
    } catch (err) {
      console.error("[handlePartialCheckout]", err)
      setCheckingOut(false)
    }
  }

  return (
    <div className="flex flex-col gap-y-4">
      <Heading level="h2" className="text-[2rem] leading-[2.75rem]">
        Summary
      </Heading>
      <DiscountCode cart={cart} />
      <Divider />
      
      {hasPartialSelection ? (
        <>
          <div className="flex flex-col gap-y-2 txt-medium text-ui-fg-subtle">
            <div className="flex items-center justify-between">
              <span>Selected items</span>
              <span className="txt-medium text-ui-fg-base font-semibold">
                {selectedCount} item{selectedCount > 1 ? "s" : ""}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span>Subtotal</span>
              <span className="txt-medium text-ui-fg-base font-semibold">
                ${(selectedSubtotal).toFixed(2)}
              </span>
            </div>
            <p className="text-xs text-ui-fg-muted mt-1">
              Shipping calculated at checkout
            </p>
          </div>
          <Divider />
          <Button
            className="w-full h-10"
            onClick={handlePartialCheckout}
            disabled={checkingOut}
          >
            {checkingOut ? "Preparing…" : `Checkout selected (${selectedCount})`}
          </Button>
          <p className="text-xs text-ui-fg-muted text-center">
            未选中商品会留在购物车
          </p>
        </>
      ) : (
        <>
          <CartTotals totals={cart} freeShippingThreshold={storeConfig.freeShippingThreshold} />
          <LocalizedClientLink
            href={"/checkout?step=" + step}
            data-testid="checkout-button"
          >
            <Button className="w-full h-10">Go to checkout</Button>
          </LocalizedClientLink>
        </>
      )}
    </div>
  )
}

export default Summary
